<template>
  <div
    class="audio-player-slider height-all flex"
    ref="refAudioBox"
    :style="renderStyle"
  >
    <slot>
      <div class="audio-player-slider-time tr">
        <span>{{ renderData.startTime }}</span>
      </div>
      <div class="audio-player-slider-center flex1">
        <div
          class="audio-player-slider-slider"
          :class="{
            loading: renderData.isLoading,
          }"
          :style="{
            '--highPointerColor':
              props.highPosition === null ||
              props.highPosition == undefined ||
              props.highPosition > renderData.duration
                ? 'none'
                : renderStyle['--progressbarColor'],
            '--highPointerLeft': getHighPositionPosition(),
          }"
          data-scrollbox
        >
          <vue-slider
            v-model="renderData.progress"
            @change="changeProgress"
            @drag-start="dragStart"
            @drag-end="dragEnd"
            @dragging="dragging"
            tooltip="none"
            :tooltipFormatter="formatTooltip"
          ></vue-slider>
          <div
            v-if="props.highPosition != null"
            class="audio-player-slider-slider-highlight-pointer"
          ></div>
        </div>
      </div>
      <div class="audio-player-slider-time tl">
        <i
          class="block icon-loading rotate-loading"
          v-if="renderData.isGenerating"
        ></i>
        <span v-else>{{
          renderData.endTime ?? renderData.duration / 1000
        }}</span>
      </div>
    </slot>
  </div>
</template>
<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  watch,
  defineExpose,
  onMounted,
  onUnmounted,
  reactive,
  getCurrentInstance,
} from "vue";
import GlobalAudioPlayer, {
  defaultPersonalizedConfig,
  lastPerContinuousPlaybackTimeData,
} from "@/components/media/audioPlayer/GlobalAudioPlayer.js";
import playIcoDark from "@/assets/img/play-web-ico.png";
import playIcoLight from "@/assets/img/play-light-ico.png";
import pauseIcoDark from "@/assets/img/pause-web-ico.png";
import pauseIcoLight from "@/assets/img/pause-light-ico.png";
import Bubble from "@/components/bubble/bubble.vue";
import VueSlider from "@/components/basic/vueSlider/VueSlider.vue";

// const VueSlider = defineAsyncComponent(() =>
//   import("vue-slider-component").then((m) => m.default)
// );

// import "vue-slider-component/theme/default.css";

import mediaUtil from "../utils/utils.js";

const props = defineProps({
  renderStyle: {
    type: Object,
    default: () => ({}),
  },

  volumeOffset: {
    type: Number,
    default: defaultPersonalizedConfig.volumeOffset,
  },
  url: {
    type: String,
    default: "",
  },
  duration: {
    type: Number,
    default: 0,
  },

  from: {
    type: Number,
    default: 0,
  },
  to: {
    type: Number,
    default: 0,
  },
  loop: {
    type: Boolean,
    default: defaultPersonalizedConfig.loop,
  },
  autoToStartIfNotCurrent: {
    type: Boolean,
    default: defaultPersonalizedConfig.autoToStartIfNotCurrent,
  },
  canSeekNotCurrent: {
    type: Boolean,
    default: defaultPersonalizedConfig.canSeekNotCurrent,
  },

  disabled: {
    type: Boolean,
    default: false,
  },
  opClickCallBack: {
    type: Function,
    default: null,
  },
  analyser: Boolean,
  switch: {
    type: Boolean,
    default: false,
  },
  highPosition: {
    type: Number,
    default: null,
  },
  isGenerating: {
    type: Boolean,
    default: false,
  },
  perContinuousPlaybackTimeCallback: {
    type: Function,
    default: () => {},
  },
  type: {
    type: String,
    default: "audio",
  },
});
const renderDefaultStyleMap = {
  "--timeColor": "#fff",
  "--timeFontSize": "12px",
  "--timeFontWeight": "400",
  "--timeMinWidth": "36px",

  "--barColor": "rgba(255, 255, 255,  0.2)",
  "--barHeight": "2px",

  "--seekbarMargin": "0px 10px 0 10px",
  "--progressbarColor": "#fff",
  "--progressSliderSize": "10px",
  "--progressSliderColor": "#fff",
  "--progressSliderHoverColor": "rgba(255, 255, 255, 0.3)",
};
const renderStyle = Object.assign(
  {},
  renderDefaultStyleMap,
  props.renderStyle ?? {}
);

const emits = defineEmits([
  "log",
  "drawEqualizer",
  "syncDuration",
  "syncPlayState",
  "syncLoadingState",
  "syncProgress",
  "switch",
  "curPlayEnded",
  "update:isGenerating",
]);
const isReady = ref(false); // 是否加载完成，可以开始播放
const isChangeProgress = ref(false);

const refAudioBox = ref(null);

const stateData = {};

const renderData = reactive({
  url: props.url,
  currentTime: 0,
  startTime: "00:00",
  endTime: "00:00",
  bufferTime: "00:00",
  duration: props.duration,
  progress: 0,
  isPlaying: false,
  isLoading: false,
  isReady: false,
  isGenerating: false,
  ifRendderSlide: true,
  mounted: false,
});
const getHighPositionPosition = () => {
  return (props.highPosition ?? 0) > (renderData.duration || 1)
    ? 100
    : ((props.highPosition ?? 0) / (renderData.duration || 1)) * 100 + "%";
};
const formatTooltip = () => {
  return mediaUtil.formatProgressToTime(
    renderData.progress,
    renderData.duration
  );
};

const analyzeStateData = {
  startPosition: 0,
};

// play or pause
const changeOp = (isplay) => {
  const appContext = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  )?.appContext;
  if (props.opClickCallBack) {
    props.opClickCallBack();
  }
  if (props.disabled) {
    appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.pause(
      renderData.url,
      true
    );
    return;
  }
  const isPlaying =
    typeof isplay === "boolean" ? isplay : !renderData.isPlaying;
  if (isPlaying) {
    appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.play(
      renderData.url,
      true
    );
  } else {
    appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.pause(
      renderData.url
    );
  }
};

const doSwitch = (data) => {
  changeOp(false);
  emits("switch", data);
};

// var playStateBeforeSeek = undefined;

const emitsPerContinuousPlaybackTimeCallback = (opType) => {
  if (
    lastPerContinuousPlaybackTimeData.url == renderData.url &&
    lastPerContinuousPlaybackTimeData.startPosition ==
      analyzeStateData.startPosition &&
    lastPerContinuousPlaybackTimeData.time ==
      renderData.progressCp * renderData.duration -
        analyzeStateData.startPosition
  ) {
    return;
  }
  if (
    renderData.progressCp * renderData.duration -
      analyzeStateData.startPosition >
    0
  ) {
    const data = {
      duration: renderData.duration,
      startPosition: analyzeStateData.startPosition,
      url: renderData.url,
      time:
        renderData.progressCp * renderData.duration -
        analyzeStateData.startPosition,
      endPosition: renderData.progressCp * renderData.duration,
      opType: opType || "changeProgress",
    };
    // console.log(
    //   "emitsPerContinuousPlaybackTimeCallback",
    //   data,
    //   lastPerContinuousPlaybackTimeData
    // );
    if (props.type == "globalPlayer") {
      props.perContinuousPlaybackTimeCallback?.(data);
      Object.assign(lastPerContinuousPlaybackTimeData, data);
    }
  }
};

const changeProgress = (value) => {
  const appContext = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  )?.appContext;
  stateData.isChangeProgress = false;

  emitsPerContinuousPlaybackTimeCallback("changeProgress");

  renderData.progress = Math.min(100, Math.max(0, value));
  renderData.progressCp = renderData.progress / 100;
  renderData.startTime = mediaUtil.formatProgressToTime(
    value,
    renderData.duration
  );
  appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.seek(
    renderData.url,
    renderData.progress / 100
  );
  touchEnd();

  analyzeStateData.startPosition = renderData.duration * renderData.progressCp;
};

const dragStart = () => {
  stateData.isChangeProgress = true;
};

const dragEnd = () => {
  stateData.isChangeProgress = false;
};

const dragging = (value) => {
  renderData.progress = Math.min(100, Math.max(0, value));
  renderData.startTime = mediaUtil.formatProgressToTime(
    value,
    renderData.duration
  );
};

const init = () => {
  const appContext = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  )?.appContext;
  if (!renderData.url) {
    return;
  }
  stateData.curPlayerData =
    appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.registerPlayUrl(
      renderData.url,
      {
        duration: props.duration ?? 0,
        clipStart: props.from,
        clipEnd: props.to,
        personalizedConfig: {
          loop: props.loop,
          autoToStartIfNotCurrent: props.autoToStartIfNotCurrent,
          canSeekNotCurrent: props.canSeekNotCurrent,
        },
        listeners: {
          onDurationChange({
            duration,
            durationTime,
            sourceDuration,
            sourceDurationTime,
          }) {
            if (renderData.url == stateData.curPlayerData?.player?.url) {
              renderData.duration = duration;
              renderData.endTime = durationTime;
              if (
                renderData.isGenerating &&
                appContext?.config?.globalProperties?.$globalMediaUtils
                  ?.globalAudioPlayer?.audio?.duration == duration &&
                duration > 0 &&
                renderData.url ==
                  appContext?.config?.globalProperties?.$globalMediaUtils
                    ?.globalAudioPlayer?.currentUrl
              ) {
                renderData.isGenerating = false;
              }
            }
          },
          onPlayStateChange(isPlaying, eventType) {
            if (renderData.url == stateData.curPlayerData?.player?.url) {
              renderData.isPlaying = isPlaying;
              if (isPlaying) {
                analyzeStateData.startPosition =
                  renderData.progressCp * renderData.duration;
              } else {
                emitsPerContinuousPlaybackTimeCallback("palyStateChange");
                analyzeStateData.startPosition =
                  renderData.progressCp * renderData.duration;
              }
            }
            // console.log("🍎🍎🍎🍎🍎", renderData.isPlaying, renderData.url);
          },
          onLoadingStateChange(isLoading) {
            if (renderData.url == stateData.curPlayerData?.player?.url) {
              renderData.isLoading = isLoading;
            }
          },
          onPositionChange: (obj) => {
            if (renderData.url == stateData.curPlayerData?.player?.url) {
              const { progress, currentTime, endTime, current } = obj;
              if (!stateData.isChangeProgress) {
                renderData.startTime = currentTime;
                renderData.endTime = endTime;
                renderData.progress = Math.min(
                  100,
                  Math.max(0, progress * 100)
                );
                renderData.currentTime = current;
              }
              renderData.progressCp = progress;
            }
          },
        },
      }
    );
  appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.registGlobalListener(
    "curPlayEnded",
    ({ currentUrl, player }) => {
      if (renderData.url == currentUrl || !renderData.url) {
        emits("curPlayEnded", { currentUrl, player });
        emitsPerContinuousPlaybackTimeCallback("curPlayEnded");
        analyzeStateData.startPosition = 0;
      }
    }
  );
  if (
    stateData.curPlayerData?.player &&
    stateData.curPlayerData?.player?.url ==
      appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer
        ?.currentUrl
  ) {
    renderData.isPlaying = stateData.curPlayerData?.player?.isPlaying;
    const { progress, currentTime, endTime } =
      appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.getPositionData(
        stateData.curPlayerData?.player
      );
    const { duration } =
      appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.getDuration(
        stateData.curPlayerData?.player
      );
    renderData.duration = duration;

    renderData.progress = Math.min(100, Math.max(0, progress * 100));
    renderData.progressCp = progress;
    renderData.startTime = currentTime;
    renderData.endTime = endTime;

    emits("syncPlayState", renderData.isPlaying);
    emits("syncProgress", renderData.progress, renderData.startTime);
    emits("syncDuration", renderData.duration);
  }
  // console.log("init🍎苹果", stateData.curPlayerData);
};

const handleTouchStart = () => {};

const touchEnd = () => {
  // if (jsBridge.inTiangongApp) {
  //   jsBridge("event.requestGesture", { eventTye: "horizontal", action: "release" })
  // }
};

watch(
  () => renderData.isPlaying,
  (newValue) => {
    emits("syncPlayState", renderData.isPlaying);
  }
);
watch(
  () => renderData.isLoading,
  (newValue) => {
    emits("syncLoadingState", renderData.isLoading);
  }
);
watch(
  () => renderData.progress,
  (newValue) => {
    emits(
      "syncProgress",
      renderData.progress,
      renderData.startTime,
      renderData.currentTime
    );
  }
);

watch(
  () => props.disabled,
  (newValue) => {
    if (props.disabled) {
      const appContext = (
        getCurrentInstance() || window?.$getVueCurrentInstance?.()
      )?.appContext;
      console.log("props.disabled", props.disabled);
      appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.pause(
        renderData.url,
        true
      );
    }
  }
);

watch(
  () => {
    return props.duration;
  },
  () => {
    const appContext = (
      getCurrentInstance() || window?.$getVueCurrentInstance?.()
    )?.appContext;
    if (
      renderData.url ==
        appContext?.config?.globalProperties?.$globalMediaUtils
          ?.globalAudioPlayer?.currentUrl &&
      renderData.duration != props.duration
    ) {
      appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.setDuration(
        renderData.url,
        props.duration
      );
    }
    renderData.duration = props.duration;
    renderData.endTime = mediaUtil.formatTime(props.duration);
  }
);

const rerenderSlide = () => {
  stateData.isChangeProgress = false;
  nextTick().then(() => {
    stateData.isChangeProgress = false;
    renderData.ifRendderSlide = false;
    nextTick(() => {
      stateData.ifRendderSlide = true;
    });
  });
};

watch(
  () => {
    return props.url;
  },
  () => {
    const appContext = (
      getCurrentInstance() || window?.$getVueCurrentInstance?.()
    )?.appContext;
    if (props.url != renderData.url) {
      if (stateData.curPlayerData) {
        appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.releasePlayer(
          stateData.curPlayerData
        );
      }
      renderData.url = props.url;
      if (renderData.mounted) {
        init();
      }
      rerenderSlide();
    }
  }
);

watch(
  () => {
    return props.loop;
  },
  () => {
    const appContext = (
      getCurrentInstance() || window?.$getVueCurrentInstance?.()
    )?.appContext;
    appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.setLoop(
      renderData.url,
      props.loop
    );
  }
);

watch(
  () => {
    return props.isGenerating;
  },
  (newValue) => {
    renderData.isGenerating = props.isGenerating;
  }
);

watch(
  () => {
    return renderData.isGenerating;
  },
  (newValue) => {
    emits("update:isGenerating", renderData.isGenerating);
    console.log(
      "🍎🍎🍎🍎🍎🍎🍎🍎🍎🍎🍎renderData.isGenerating",
      renderData.isGenerating
    );
  }
);

onMounted(() => {
  const appContext = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  )?.appContext;
  renderData.mounted = true;
  renderData.url = props.url;
  renderData.duration = props.duration;
  renderData.endTime = mediaUtil.formatTime(props.duration);
  renderData.isGenerating = props.isGenerating;
  init();
  stateData.playerCacheData = {
    uuid: appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.getUuid(),
    pause() {
      changeOp(false);
    },
    getContainer() {
      return refAudioBox.value;
    },
  };
});

onUnmounted(() => {
  const appContext = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  )?.appContext;
  renderData.mounted = false;
  appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.releasePlayer(
    stateData.curPlayerData
  );
});

defineExpose({
  changeOp(isPlay) {
    return new Promise((resolve, reject) => {
      changeOp(isPlay);
      resolve(renderData.isPlaying);
    });
  },
  changeProgress,
  renderData,
  checkAudioStatus: () => {
    const appContext = (
      getCurrentInstance() || window?.$getVueCurrentInstance?.()
    )?.appContext;
    renderData.isPlaying =
      appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer
        ?.players[renderData.url]?.isPlaying || renderData.isPlaying;
    emits("syncPlayState", renderData.isPlaying);
  },
  triggleEmitsPerContinuousPlaybackTimeCallback(opType) {
    emitsPerContinuousPlaybackTimeCallback(opType);
  },
  init: () => {
    const appContext = (
      getCurrentInstance() || window?.$getVueCurrentInstance?.()
    )?.appContext;
    if (stateData.curPlayerData) {
      appContext?.config?.globalProperties?.$globalMediaUtils?.globalAudioPlayer?.releasePlayer(
        stateData.curPlayerData
      );
    }
    renderData.url = props.url;
    renderData.duration = props.duration;
    renderData.endTime = mediaUtil.formatTime(props.duration);
    console.log("🍎renderData.url🍎", renderData.url);
    init();
    rerenderSlide();
  },
});
</script>
<style lang="scss">
.audio-player-slider {
  align-items: center;
  .audio-player-slider-time {
    font-family: PingFang SC-Regular;
    font-size: var(--timeFontSize);
    font-weight: var(--timeFontWeight);
    color: var(--timeColor);
    min-width: var(--timeMinWidth);
    letter-spacing: 0.075em;
    i.icon-loading.block {
      width: calc(var(--opIcoSize));
      height: calc(var(--opIcoSize));
      background: url(@/assets/img/loading.png);
      background-size: 100% 100%;
    }
  }
  .audio-player-slider-center {
    margin-left: 4px;
    margin: var(--seekbarMargin);
  }
  .audio-player-slider-slider {
    position: relative;
    .audio-player-slider-slider-highlight-pointer {
      position: absolute;
      left: var(--highPointerLeft);
      top: 50%;
      width: 4px;
      height: 4px;
      transform: translateY(-50%);
      border-radius: 50%;
      background: linear-gradient(135deg, #4fdeff 0%, #e675ff 100%);
    }
  }
  .render-disabled--true {
    opacity: 0.5;
    .audio-player-slider-op {
      cursor: not-allowed;
    }
    .audio-player-slider-op:hover {
      background-color: transparent;

      .apo.pointer:hover {
        cursor: not-allowed;
        background: transparent;
      }
    }
  }
  .render-disabled-extend--false {
    opacity: 1;
    .audio-player-slider-center {
      cursor: not-allowed;
      opacity: 0.5;
    }
    .audio-player-slider-time {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
